import { useCallback } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import Heading from "@atlaskit/heading";
import { token } from "@atlaskit/tokens";

import { UbpStep } from "@/components/usage-based-pricing-calculator/src/context";
import {
    useInputValues,
    useInputSetters,
    useStep,
    useSetStep
} from "@/components/usage-based-pricing-calculator/src/hooks";
import { Button } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";

import {
    useAiAutomationTracking,
    useAiChatAgentsTracking,
    useIndexedObjectsTracking
} from "../hooks/tracking";

import { messages } from "./messages";

const sectionStyles = css({
    paddingBlock: token("space.400")
});

const titleWrapperStyles = css({
    borderBlockEnd: `1px solid ${wacToken("color.neutral.light300")}`
});

const titleStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "26px",
    color: wacToken("color.primary.black"),
    marginBlockEnd: token("space.100")
});

const subtitleStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "16px",
    lineHeight: "24px",
    color: wacToken("color.neutral.light700"),
    marginBlockEnd: token("space.300")
});

const cardsContainerStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.200"),
    marginBlockEnd: token("space.400")
});

const featureCardStyles = css({
    backgroundColor: wacToken("color.neutral.light0"),
    border: `1px solid ${wacToken("color.neutral.light300")}`,
    borderRadius: "12px",
    overflow: "hidden",
    display: "flex"
});

const accentBarStyles = css({
    width: "4px",
    flexShrink: 0
});

const accentBarYellowStyles = css({
    backgroundColor: wacToken("color.primary.yellow")
});

const accentBarBlueStyles = css({
    backgroundColor: wacToken("color.primary.blue")
});

const cardContentStyles = css({
    padding: token("space.250"),
    flex: 1
});

const cardTitleStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: 600,
    fontSize: "16px",
    color: wacToken("color.primary.black"),
    marginBlockEnd: token("space.200")
});

const dropdownsRowStyles = css({
    display: "flex",
    gap: token("space.200"),
    flexWrap: "wrap"
});

const dropdownGroupStyles = css({
    flex: 1,
    minWidth: "140px"
});

const dropdownLabelStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "12px",
    color: wacToken("color.neutral.light700"),
    marginBlockEnd: token("space.100"),
    display: "block"
});

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
const dropdownStyles = css({
    "width": "100%",
    "appearance": "none",
    "backgroundColor": wacToken("color.neutral.light100"),
    "border": `1px solid ${wacToken("color.neutral.light300")}`,
    "borderRadius": "20px",
    "padding": "8px 32px 8px 16px",
    "fontFamily": wacToken("font.charlie.text"),
    "fontSize": "16px",
    "color": wacToken("color.primary.black"),
    "cursor": "pointer",
    "backgroundImage":
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236b6e76' stroke-width='2' fill='none'/%3E%3C/svg%3E\")",
    "backgroundRepeat": "no-repeat",
    "backgroundPosition": "right 12px center",
    "&:focus": {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: "2px"
    }
});

const buttonContainerStyles = css({
    display: "flex",
    justifyContent: "center"
});

export const FeatureEstimation = () => {
    const intl = useIntl();
    const step = useStep();
    const setStep = useSetStep();
    const inputs = useInputValues();
    const setters = useInputSetters();
    const { fireChatAdoptionChangedEvent, fireChatQueriesPerUserChangedEvent } =
        useAiChatAgentsTracking();
    const {
        fireAutomationActiveRulesChangedEvent,
        fireAutomationRunsPerRuleChangedEvent
    } = useAiAutomationTracking();
    const {
        fireIndexed3rdPartyToolsChangedEvent,
        fireIndexedObjectsPerToolChangedEvent
    } = useIndexedObjectsTracking();

    const handleNext = useCallback(() => {
        setStep(UbpStep.plans);
    }, [setStep]);

    if (step < UbpStep.usageEstimate) {
        return null;
    }

    return (
        <div css={sectionStyles}>
            <div css={titleWrapperStyles}>
                <Heading size="medium" as="h2">
                    <span css={titleStyles}>
                        <FormattedMessage
                            {...messages.featureEstimationTitle}
                        />
                    </span>
                </Heading>
            </div>
            <p css={subtitleStyles}>
                <FormattedMessage {...messages.featureEstimationSubtitle} />
            </p>

            <div css={cardsContainerStyles}>
                {/* AI Chat & Agents */}
                <div css={featureCardStyles}>
                    <div css={[accentBarStyles, accentBarYellowStyles]} />
                    <div css={cardContentStyles}>
                        <Heading size="small" as="h3">
                            <span css={cardTitleStyles}>
                                <FormattedMessage
                                    {...messages.aiChatAgentsTitle}
                                />
                            </span>
                        </Heading>
                        <div css={dropdownsRowStyles}>
                            <div css={dropdownGroupStyles}>
                                <label
                                    css={dropdownLabelStyles}
                                    htmlFor="chatAdoptionPercent"
                                >
                                    <FormattedMessage
                                        {...messages.expectedAdoptionLabel}
                                    />
                                </label>
                                <select
                                    css={dropdownStyles}
                                    id="chatAdoptionPercent"
                                    aria-label={intl.formatMessage(
                                        messages.expectedAdoptionLabel
                                    )}
                                    value={inputs.chatAdoptionPercent}
                                    onChange={(e) => {
                                        const value = parseFloat(
                                            e.target.value
                                        );
                                        setters.setChatAdoptionPercent(value);
                                        fireChatAdoptionChangedEvent(value);
                                    }}
                                >
                                    <option value={0.25}>25</option>
                                    <option value={0.5}>50</option>
                                    <option value={0.75}>75</option>
                                    <option value={1}>100</option>
                                </select>
                            </div>
                            <div css={dropdownGroupStyles}>
                                <label
                                    css={dropdownLabelStyles}
                                    htmlFor="chatUsersPerRulePerMonth"
                                >
                                    <FormattedMessage
                                        {...messages.queriesPerUserLabel}
                                    />
                                </label>
                                <select
                                    css={dropdownStyles}
                                    id="chatUsersPerRulePerMonth"
                                    aria-label={intl.formatMessage(
                                        messages.queriesPerUserLabel
                                    )}
                                    value={inputs.chatUsersPerRulePerMonth}
                                    onChange={(e) => {
                                        const value = parseInt(
                                            e.target.value,
                                            10
                                        );
                                        setters.setChatUsersPerRulePerMonth(
                                            value
                                        );
                                        fireChatQueriesPerUserChangedEvent(
                                            value
                                        );
                                    }}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* AI Automation */}
                <div css={featureCardStyles}>
                    <div css={[accentBarStyles, accentBarBlueStyles]} />
                    <div css={cardContentStyles}>
                        <Heading size="small" as="h3">
                            <span css={cardTitleStyles}>
                                <FormattedMessage
                                    {...messages.aiAutomationTitle}
                                />
                            </span>
                        </Heading>
                        <div css={dropdownsRowStyles}>
                            <div css={dropdownGroupStyles}>
                                <label
                                    css={dropdownLabelStyles}
                                    htmlFor="automationActiveRules"
                                >
                                    <FormattedMessage
                                        {...messages.activeRulesLabel}
                                    />
                                </label>
                                <select
                                    css={dropdownStyles}
                                    id="automationActiveRules"
                                    aria-label={intl.formatMessage(
                                        messages.activeRulesLabel
                                    )}
                                    value={inputs.automationActiveRules}
                                    onChange={(e) => {
                                        const value = parseInt(
                                            e.target.value,
                                            10
                                        );
                                        setters.setAutomationActiveRules(value);
                                        fireAutomationActiveRulesChangedEvent(
                                            value
                                        );
                                    }}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </select>
                            </div>
                            <div css={dropdownGroupStyles}>
                                <label
                                    css={dropdownLabelStyles}
                                    htmlFor="automationRunsPerRulePerMonth"
                                >
                                    <FormattedMessage
                                        {...messages.runsPerRuleLabel}
                                    />
                                </label>
                                <select
                                    css={dropdownStyles}
                                    id="automationRunsPerRulePerMonth"
                                    aria-label={intl.formatMessage(
                                        messages.runsPerRuleLabel
                                    )}
                                    value={inputs.automationRunsPerRulePerMonth}
                                    onChange={(e) => {
                                        const value = parseInt(
                                            e.target.value,
                                            10
                                        );
                                        setters.setAutomationRunsPerRulePerMonth(
                                            value
                                        );
                                        fireAutomationRunsPerRuleChangedEvent(
                                            value
                                        );
                                    }}
                                >
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={250}>250</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Indexed Objects */}
                <div css={featureCardStyles}>
                    <div css={[accentBarStyles, accentBarBlueStyles]} />
                    <div css={cardContentStyles}>
                        <Heading size="small" as="h3">
                            <span css={cardTitleStyles}>
                                <FormattedMessage
                                    {...messages.indexedObjectsTitle}
                                />
                            </span>
                        </Heading>
                        <div css={dropdownsRowStyles}>
                            <div css={dropdownGroupStyles}>
                                <label
                                    css={dropdownLabelStyles}
                                    htmlFor="indexedObjects3rdPartyTools"
                                >
                                    <FormattedMessage
                                        {...messages.thirdPartyToolsLabel}
                                    />
                                </label>
                                <select
                                    css={dropdownStyles}
                                    id="indexedObjects3rdPartyTools"
                                    aria-label={intl.formatMessage(
                                        messages.thirdPartyToolsLabel
                                    )}
                                    value={inputs.indexedObjects3rdPartyTools}
                                    onChange={(e) => {
                                        const value = parseInt(
                                            e.target.value,
                                            10
                                        );
                                        setters.setIndexedObjects3RdPartyTools(
                                            value
                                        );
                                        fireIndexed3rdPartyToolsChangedEvent(
                                            value
                                        );
                                    }}
                                >
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={75}>75</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                            <div css={dropdownGroupStyles}>
                                <label
                                    css={dropdownLabelStyles}
                                    htmlFor="indexedObjectsPerTool"
                                >
                                    <FormattedMessage
                                        {...messages.objectsPerToolLabel}
                                    />
                                </label>
                                <select
                                    css={dropdownStyles}
                                    id="indexedObjectsPerTool"
                                    aria-label={intl.formatMessage(
                                        messages.objectsPerToolLabel
                                    )}
                                    value={inputs.indexedObjectsObjectsPerToo}
                                    onChange={(e) => {
                                        const value = parseInt(
                                            e.target.value,
                                            10
                                        );
                                        setters.setIndexedObjectsObjectsPerToo(
                                            value
                                        );
                                        fireIndexedObjectsPerToolChangedEvent(
                                            value
                                        );
                                    }}
                                >
                                    <option value={500}>500</option>
                                    <option value={1000}>1000</option>
                                    <option value={2500}>2500</option>
                                    <option value={5000}>5000</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div css={buttonContainerStyles}>
                <Button variant="primary" size="large" onClick={handleNext}>
                    <FormattedMessage
                        {...messages.featureEstimationNextButton}
                    />
                </Button>
            </div>
        </div>
    );
};
